import { useState, useCallback } from 'react';

import { FileUploadError } from './fragments/MobileActionBar/MobileActionBar.props';

interface UseMobileDetailActionsParams {
  handleNoteRefresh: () => Promise<void>;
  handlePartActionRefresh: () => Promise<void>;
  uploadFiles: (files: File[]) => Promise<unknown>;
}

export const useMobileDetailActions = ({
  handleNoteRefresh,
  handlePartActionRefresh,
  uploadFiles,
}: UseMobileDetailActionsParams) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleNoteAdded = useCallback(async (): Promise<void> => {
    try {
      await handleNoteRefresh();
      setSuccessMessage('Note added successfully');
    } catch {
      setErrorMessage('Failed to refresh notes. Please try again.');
    }
  }, [handleNoteRefresh]);

  const handlePartActionComplete = useCallback(async (): Promise<void> => {
    try {
      await handlePartActionRefresh();
      setSuccessMessage('Part action recorded successfully');
    } catch {
      setErrorMessage('Failed to refresh part actions. Please try again.');
    }
  }, [handlePartActionRefresh]);

  const handleTakePhoto = useCallback(
    async (file: File): Promise<void> => {
      try {
        await uploadFiles([file]);
        setSuccessMessage('Photo uploaded successfully');
      } catch {
        setErrorMessage('Failed to upload photo. Please try again.');
      }
    },
    [uploadFiles]
  );

  const handleUploadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      try {
        await uploadFiles(files);
        const fileWord = files.length === 1 ? 'file' : 'files';
        setSuccessMessage(`${files.length} ${fileWord} uploaded successfully`);
      } catch {
        setErrorMessage('Failed to upload files. Please try again.');
      }
    },
    [uploadFiles]
  );

  const handleUploadError = useCallback((error: FileUploadError): void => {
    setErrorMessage(error.message);
  }, []);

  const handleCloseError = useCallback((): void => {
    setErrorMessage(null);
  }, []);

  const handleCloseSuccess = useCallback((): void => {
    setSuccessMessage(null);
  }, []);

  return {
    errorMessage,
    successMessage,
    handleNoteAdded,
    handlePartActionComplete,
    handleTakePhoto,
    handleUploadFiles,
    handleUploadError,
    handleCloseError,
    handleCloseSuccess,
  };
};
